import { SelectAgriFieldAndPlanting, Planting, AgriField } from '@/types/Component';
import moment from 'moment';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from "@/assets/common/Modal/Modal.vue";
import { toCamel } from 'snake-camel';

type PreSelectItem = {
    agriFieldId: string,
    plantingId: string,
}

@Component({
    components: { Modal }
})
export default class SelectFromPlantings extends Vue {
    @Prop()
    private value?: boolean;

    @Emit('input')
    input(value: boolean): void {
        value
    }

    @Prop()
    private preSelectItem?: PreSelectItem;

    @Prop()
    private pdTenantId?: string;

    @Prop()
    private sitemId?: string;

    @Prop()
    private workDate?: Date;

    @Prop()
    private showSelectFrom?: boolean;

    @Emit('selectedItem')
    selectedItem(): SelectAgriFieldAndPlanting {
        this.input(false)
        return { agriField: this.selectedField, planting: this.preSelectedPlanting }
    }

    private outOfTerm = false;
    private searchWord = '';

    private plantings: Array<Planting> = []
    private filteredPlantings: Array<Planting> = []
    private displayPlantings: Array<Planting> = []

    private fromPlantingList: Partial<Record<string, Array<Planting>>> = {}
    private selectedCrop = '';
    private selectedPlanting = { id: '', name: '', cropFamicId: '' };
    private selectedField = { id: '', name: '', plantedArea: 0 };
    private preSelectedPlanting = { id: '', name: '', cropFamicId: '' }
    private displayAgriFields: Array<AgriField> = []

    private sortByPlantingFlag = 1
    private sortByTermFlag = 1
    private sortByAgriFieldFlag = 1
    private sortByAgriFieldTermFlag = 1

    @Watch('value')
    async whenOpen(): Promise<void> {
        if (this.value) {
            const url = this.pdTenantId ? `/plantings/plantings_by_cropId?tenant_id=${this.pdTenantId}&sitem_id=${this.sitemId}` : '/plantings';
            const res = toCamel(await this.$apiClient.doRequest('get', url)) as { plantings: Planting[] };
            this.plantings = res.plantings

            const filterByPlanting = res.plantings.filter((p: Planting) => {
                return p.agriFields.length !== 0 && this.isWithinTheTerm(p.workStartDate, p.workEndDate)
            }).map((p: Planting) => {
                const agriField = p.agriFields.filter(af => {
                    return this.isWithinTheTerm(af.displayStart, af.displayEnd)
                })
                return { ...p, agriFields: agriField }
            })

            this.filteredPlantings = filterByPlanting

            this.selectedCrop = 'すべて'

            if (this.preSelectItem !== undefined && this.preSelectItem?.agriFieldId !== '' && this.preSelectItem?.plantingId !== '') {
                const { plantingId, agriFieldId } = this.preSelectItem

                // 期間外もしくは非表示かどうか（期間内には存在しないかどうか）
                const outOfTermOrIsHideen = !this.filteredPlantings.some(p => p.id === plantingId && p.agriFields.some(af => af.id === agriFieldId))

                const planting = outOfTermOrIsHideen ? this.plantings.find(p => p.id === plantingId) : this.filteredPlantings.find(p => p.id === plantingId)
                const agriField = planting?.agriFields.find(af => af.id === agriFieldId)
                // planting が非表示ではなく、圃場も非表示ではない場合
                if (planting && agriField) {
                    this.selectedPlanting = this.preSelectedPlanting = { id: planting.id, name: planting.name, cropFamicId: planting.cropFamicId }
                }
                // planting が非表示ではなく、圃場が非表示であり、表示する圃場が期間内に存在する場合
                else if (planting && planting.agriFields.some(af => this.isWithinTheTerm(af.displayStart, af.displayEnd))) {
                    this.selectedPlanting = this.preSelectedPlanting = { id: planting.id, name: planting.name, cropFamicId: planting.cropFamicId }
                }
                // 非表示かつ filteredPlantings が空配列ではない場合
                else if (this.filteredPlantings.length > 0) {
                    this.filteringPlanting()
                    this.selectPlanting(this.displayPlantings[0])
                }
                // 非表示かつ filteredPlantings が空配列の場合
                else {
                    this.selectedPlanting = this.preSelectedPlanting = { id: '', name: '', cropFamicId: '' }
                }
                this.selectedField = agriField ? { id: agriField.id, name: agriField.name, plantedArea: agriField.plantedArea } : { id: '', name: '', plantedArea: 0 }

                /*
                outOfTerm が false になる条件
                作付と圃場がどちらも期間内
                作付が非表示
                作付が期間内かつ圃場が非表示
                圃場が非表示かつ作付に紐づく圃場が非表示しか存在しない
                */
                this.outOfTerm = !(
                    !outOfTermOrIsHideen ||
                    planting === undefined ||
                    this.filteredPlantings.some(p => p.id === plantingId) && agriField === undefined ||
                    agriField === undefined && planting.agriFields.length === 0
                )
                this.filteringPlanting()
            } else {
                this.outOfTerm = false
                this.filteringPlanting()
                if (this.filteredPlantings.length > 0) {
                    this.selectPlanting(this.displayPlantings[0])
                } else {
                    this.selectedPlanting = { id: '', name: '', cropFamicId: '' }
                }
            }
        }
        // モーダルクローズ時
        else {
            this.resetSelectItem()
        }
    }

    // 選択状態と作付表示の初期化
    private resetSelectItem() {
        this.selectedCrop = 'すべて'
        this.selectedField = { id: '', name: '', plantedArea: 0 }
        this.selectedPlanting = { id: '', name: '', cropFamicId: '' }
        this.preSelectedPlanting = { id: '', name: '', cropFamicId: '' }
        this.outOfTerm = false
        this.displayPlantings = []
        this.displayAgriFields = []
        this.searchWord = ''
    }

    private isWithinTheTerm(start: Date | null | undefined, end: Date | null | undefined) {
        const workDate = moment(this.workDate)
        const startDate = moment(start)
        const endDate = moment(end)
        return (!startDate.isValid() || workDate.isSameOrAfter(startDate)) && (!endDate.isValid() || workDate.isSameOrBefore(endDate))
    }

    private groupByCrop(plantings: Array<Planting>) {
        return plantings.reduce((group, p) => {
            if (group[p.crop.name] === undefined) {
                group[p.crop.name] = []
            }
            group[p.crop.name]?.push(p)
            return group
        }, {} as Partial<Record<string, Array<Planting>>>)
    }

    private formatWorkDate(date: Date) {
        return moment(date).format('YYYY年M月D日')
    }

    private selectCrop(key: string) {
        this.selectedCrop = key
        this.displayAgriFields = []
        this.selectedPlanting = { id: '', name: '', cropFamicId: '' }
        this.selectedField = { id: '', name: '', plantedArea: 0 }
    }

    private selectPlanting(planting: {cropFamicId: any;id: string, name: string}) {
        const displayFirstPlanting = this.displayPlantings[0] ? { id: this.displayPlantings[0].id, name: this.displayPlantings[0].name, cropFamicId: this.displayPlantings[0].cropFamicId} : { id: "", name: "", cropFamicId: '' }
        const selectedPlanting = planting ? { id: planting.id, name: planting.name, cropFamicId: planting.cropFamicId } : displayFirstPlanting
        this.selectedPlanting = selectedPlanting
        const selected = this.displayPlantings?.find(p => p.id === this.selectedPlanting.id)?.agriFields
        this.displayAgriFields = selected ? selected : []
    }

    private selectField(id: string, name: string, plantedArea: number) {
        this.preSelectedPlanting = this.selectedPlanting
        this.selectedField = { id, name, plantedArea }
    }

    private checkedField(id: string) {
        return this.selectedField.id === id && this.preSelectedPlanting.id === this.selectedPlanting.id
        ? 'checked'
        : ''
    }

    private backupSelectedField = { id: "", name: "", plantedArea: 0 };
    private filteringPlanting() {
        this.backupSelectedField = this.selectedField;
        this.displayPlantings = this.outOfTerm ? this.plantings : this.filteredPlantings
        this.displayPlantings = this.displayPlantings.map(p => {
            const agriField = p.agriFields.filter(af => af.name.indexOf(this.searchWord) > -1)
            return { ...p, agriFields: agriField }
        }).filter(p => p.agriFields.length !== 0)
        if (this.displayPlantings.length > 0) {
            const isFieldSelected = this.displayPlantings.some(planting =>{
                return planting.agriFields.some(val=>val.id == this.selectedField.id)
            })
            if (!isFieldSelected) {
                this.selectField("","",0)
            }
        } else {
            this.selectField("", "", 0)
        }
        this.fromPlantingList = { 'すべて': this.displayPlantings, ...this.groupByCrop(this.displayPlantings) }
        if (!this.fromPlantingList[this.selectedCrop]) {
            // 期間外の作目を選択している際に「期間外も表示」のチェックを外した場合に作目に「すべて」を選択する
            this.selectedCrop = 'すべて'
        }
        this.selectPlanting(this.selectedPlanting)
    }

    private sortPlantingsByName(plantings: Planting[] | undefined) {
        return plantings?.sort((a, b) => a.name > b.name ? this.sortByPlantingFlag : -this.sortByPlantingFlag)
    }

    private sortByPlanting() {
        this.sortPlantingsByName(this.displayPlantings)
        if (this.selectedCrop) {
            this.sortPlantingsByName(this.fromPlantingList[this.selectedCrop])
        }
        this.sortByPlantingFlag = -this.sortByPlantingFlag
    }

    private sortPlantingsByTerm(plantings: Planting[] | undefined) {
        return plantings?.sort((a, b) =>
            moment(a.workStartDate).isValid() && moment(b.workStartDate).isValid()
                ? moment(a.workStartDate).isSameOrBefore(moment(b.workStartDate))
                    ? this.sortByTermFlag
                    : -this.sortByTermFlag
                : 1
        )
    }

    private sortByTerm() {
        this.sortPlantingsByTerm(this.displayPlantings)
        if (this.selectedCrop) {
            this.sortPlantingsByTerm(this.fromPlantingList[this.selectedCrop])
        }
        this.sortByTermFlag = -this.sortByTermFlag
    }

    private sortByAgriField() {
        this.displayAgriFields.sort((a, b) => (
            a.name > b.name ? this.sortByAgriFieldFlag : -this.sortByAgriFieldFlag
        ))
        this.sortByAgriFieldFlag = -this.sortByAgriFieldFlag
    }

    private sortByAgriFieldTerm() {
        this.displayAgriFields.sort((a, b) =>
            moment(a.displayStart).isValid() && moment(b.displayStart).isValid()
                ? moment(a.displayStart).isSameOrBefore(moment(b.displayStart))
                    ? this.sortByAgriFieldTermFlag
                    : -this.sortByAgriFieldTermFlag
                : 1
        )
        this.sortByAgriFieldTermFlag = -this.sortByAgriFieldTermFlag
    }

    private cancel() {
        this.input(false)
    }

    get isModalVisible(): boolean {
        return this.value ? this.value : false
    }

    set isModalVisible(value: boolean) {
        this.input(value)
    }
}
